import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Clock, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// Shown to employers/agencies: 30-day free trial status → upgrade to a plan.
export default function TrialBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const trial = user?.trial;
  if (!trial || !trial.on_trial) return null;

  const upgrade = () => navigate("/upgrade");

  if (!trial.active) {
    return (
      <div data-testid="trial-banner-expired" className="liquid-glass rounded-xl border border-amber-500/30 p-4 flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-300 shrink-0"><AlertTriangle size={18} /></div>
          <div>
            <div className="text-slate-100 font-medium text-sm">{t("trial.expiredTitle")}</div>
            <div className="text-slate-400 text-xs mt-0.5">{t("trial.expiredMsg")}</div>
          </div>
        </div>
        <Button onClick={upgrade} data-testid="trial-upgrade-button" className="bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold shrink-0">
          <Sparkles size={15} className="mr-1.5" /> {t("trial.upgrade")}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="trial-banner" className="liquid-glass rounded-xl border border-teal-500/25 p-3.5 flex items-center justify-between gap-4 mb-6">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-teal-300 shrink-0"><Clock size={18} /></div>
        <div>
          <div className="text-slate-100 font-medium text-sm">{t("trial.label")} · <span className="text-teal-300">{t("trial.daysLeft", { n: trial.days_left })}</span></div>
          <div className="text-slate-400 text-xs mt-0.5">{t("trial.note")}</div>
        </div>
      </div>
      <Button variant="outline" onClick={upgrade} data-testid="trial-upgrade-button" className="border-teal-500/40 text-teal-200 hover:bg-teal-500/10 shrink-0">
        <Sparkles size={15} className="mr-1.5" /> {t("trial.upgrade")}
      </Button>
    </div>
  );
}
